import {CommonModule} from '@angular/common';
import {Component, Input, TemplateRef} from '@angular/core';
import {ColumnDefinition, SessionAnalyticsFact, VehicleLogRow} from '../../../models/vte';
import {resolveAccessDecisionBadge} from '../../../services/vte/vte.constants';
import {AnalyticsFactRendererComponent} from '../analytics-fact-renderer/analytics-fact-renderer.component';
import {InspectionOutcomeBadgeComponent} from '../inspection-outcome-badge/inspection-outcome-badge.component';
import {TransactionStatusBadgeComponent} from '../transaction-status-badge/transaction-status-badge.component';
import {
    extractFactValue,
    getAccessDecision,
    getInspectionOutcome,
    wasProviderExpectedForVehicleLog
} from '../vehicle-log-vte.utils';

@Component({
    selector: 'app-dynamic-column-host',
    standalone: true,
    imports: [
        CommonModule,
        InspectionOutcomeBadgeComponent,
        TransactionStatusBadgeComponent,
        AnalyticsFactRendererComponent,
    ],
    template: `
        <ng-container [ngSwitch]="column.cellType">
            <ng-container *ngSwitchCase="'inspection_outcome'">
                <app-inspection-outcome-badge [outcome]="getInspectionOutcome(row)"></app-inspection-outcome-badge>
            </ng-container>

            <ng-container *ngSwitchCase="'access_decision'">
                <span *ngIf="accessDecisionBadge as badge" class="access-decision-badge" [ngClass]="badge.variant">
                    {{ badge.label }}
                </span>
            </ng-container>

            <ng-container *ngSwitchCase="'transaction_status'">
                <app-transaction-status-badge [status]="stringValue(row, column.key)"></app-transaction-status-badge>
            </ng-container>

            <ng-container *ngSwitchCase="'analytics_fact'">
                <app-analytics-fact-renderer
                    [analyticsKey]="column.analyticsKey || ''"
                    [outputFact]="column.outputFact || ''"
                    [label]="column.label"
                    [sessionFact]="sessionAnalyticsFact(row, column.outputFact || '')"
                    [value]="sessionAnalyticsFact(row, column.outputFact || '')
                        ? null
                        : (analyticsFactValue(row, column.outputFact || '')?.value ?? null)"
                    [confidence]="sessionAnalyticsFact(row, column.outputFact || '')
                        ? null
                        : (analyticsFactValue(row, column.outputFact || '')?.confidence ?? null)"
                    [capabilityExpected]="sessionAnalyticsFact(row, column.outputFact || '')
                        ? null
                        : capabilityExpected(row, column)"
                ></app-analytics-fact-renderer>
            </ng-container>

            <ng-container *ngSwitchCase="'custom'">
                <ng-container
                    *ngTemplateOutlet="customTemplate; context: {$implicit: row, column: column}"
                ></ng-container>
            </ng-container>

            <ng-container *ngSwitchDefault>
                {{ stringValue(row, column.key) }}
            </ng-container>
        </ng-container>
    `,
    styles: [`
        .access-decision-badge {
            display: inline-flex;
            align-items: center;
            padding: 0.2rem 0.55rem;
            border-radius: 999px;
            font-size: 0.72rem;
            font-weight: 700;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            line-height: 1.2;
            white-space: nowrap;
        }

        .access-decision-badge.allow { background: #DCFCE7; color: #16A34A; }
        .access-decision-badge.deny { background: #FEE2E2; color: #DC2626; }
        .access-decision-badge.neutral { background: #E5E7EB; color: #374151; }
    `],
})
export class DynamicColumnHostComponent {
    @Input({required: true}) column!: ColumnDefinition;
    @Input({required: true}) row!: VehicleLogRow;
    @Input() customTemplate: TemplateRef<unknown> | null = null;

    get accessDecisionBadge() {
        return resolveAccessDecisionBadge(getAccessDecision(this.row));
    }

    getInspectionOutcome(row: VehicleLogRow): string | null {
        return getInspectionOutcome(row);
    }

    stringValue(row: VehicleLogRow, key: string): string {
        const value = row?.[key];
        if (value === null || value === undefined) {
            return '';
        }
        return String(value);
    }

    analyticsFactValue(row: VehicleLogRow, outputFact: string) {
        return extractFactValue(row, outputFact);
    }

    /**
     * Prefer completed Dashboard session_analytics DTO when present.
     * Renderer must not reconstruct Entry/Exit from provider_metadata.
     */
    sessionAnalyticsFact(row: VehicleLogRow, outputFact: string): SessionAnalyticsFact | null {
        if (!outputFact?.trim()) {
            return null;
        }
        const map = row?.session_analytics;
        if (!map || typeof map !== 'object') {
            return null;
        }
        const fact = map[outputFact];
        if (!fact || typeof fact !== 'object' || !fact.overall) {
            return null;
        }
        return fact;
    }

    capabilityExpected(row: VehicleLogRow, column: ColumnDefinition): boolean | null {
        return wasProviderExpectedForVehicleLog(row, column.providerId || null);
    }
}
